/*
    When making changes to existing logs, remember to update our saved Athena queries in the web_app workgroups if needed.
*/

import { makePrivateRequestAsUser } from "./managers/requestBuilder";
import { getUid } from "./managers/store";

import { getUserLocation } from "./user";

import { getBrowserName, getDeviceModel, getDeviceType, getFingerprintId, getOSName } from "../utils/browser";
import { isLegacyMobileApp, isMobileApp } from "../utils/getHost";
import { WEB_APP_VERSION } from "../utils/version";
import { SHARING_LINK_NEVER_EXPIRE } from "./jwt";
import { getSanitisedHref } from "../utils/url";

// Instance id
// Random number assigned to this instance to identify it in logs when user has multiple tabs open
const INSTANCE_ID = Math.floor(Math.random() * 10000);
window.INSTANCE_ID = INSTANCE_ID; // Can be used to identify id through console

// Time web app first ran
const INSTANCE_START = new Date().getTime();

/**
 * All Athena logging goes through this function.
 * @param {string} type
 * @param {object} val
 */
export const athenaAuthRequest = makePrivateRequestAsUser(async ({ makePrivateRequest }, type, val) => {
    const payloadVal = {
        host: window.location.host,
        fingerprint: await getFingerprintId(),
        instance: INSTANCE_ID,
        instanceStart: INSTANCE_START,
        webAppVersion: WEB_APP_VERSION,
        url: getSanitisedHref(),
        app: isMobileApp,
        legacyApp: isLegacyMobileApp,
        windowHeight: window.innerHeight,
        windowWidth: window.innerWidth,
        ...val
    };
    if (
        !window.location.hostname?.match(/localhost/) &&

        // Must be logged in
        getUid()
    ) {
        const sendRequest = async (attempts = 0) => {
            try {
                
                await makePrivateRequest('auth', 'POST', `/tracking/:uid/0/alog`, {
                    data: {
                        payload: {
                            type,
                            val: payloadVal
                        }
                    }
                });
            } catch (error) {
                console.error(error);
                if (attempts === 4) {
                    console.warn(`Athena log of type ${type} lost after max attempts reached`);
                } else if (error?.message === 'Network Error') {
                    console.log(`Attempt ${attempts + 1} to send Athena log of type ${type} failed to send due to network error so will retry.`);
                    setTimeout(() => sendRequest(attempts + 1), 60000 * Math.pow(2, attempts));
                }
            }
        }

        sendRequest();
    } else if (getUid() && process.env.NODE_ENV !== 'test') {
        console.log('Athena log:', type, payloadVal);
    }
});

// Temporary log to try to work out how many users can play H265
const logH265Support = async () => {

    const codecs = ['hvc1.1.6.L93.B0', 'hvc1.1.6.L120.90', 'hvc1.2.4.L120.90'].reduce((acc, codec) => ({
        ...acc,
        [codec]: document.createElement('video').canPlayType(`video/mp4; codecs="${codec}"`)
    }), {});

    return athenaAuthRequest(
        'h265_support',
        {
            canPlay: codecs['hvc1.1.6.L93.B0'], // For backwards compatibility
            ...codecs,
            browser: getBrowserName(),
            os: getOSName(),
            deviceModel: getDeviceModel(),
            deviceType: getDeviceType()
        }
    );
}

/**
 * Log user login or token refresh to Athena.
 * @param {boolean} newSession Is this a new session (i.e. has the web app just started up or been logged in to) or are we just refreshing a token?
 * @param {('password', 'sso', 'loginToken', 'demo')} type Type of login or token refresh. If `newSession` is false, this will always be "loginToken".
 * @param {string} seriesId Series id of login token returned.
 * @returns 
 */
export const logLogin = async (newSession, type, seriesId) => {

    // Temporary
    if (newSession) {
        logH265Support();
    }

    const userLocation = await getUserLocation(false);

    return athenaAuthRequest(
        'web_login',
        {
            newSession,
            type: type,
            seriesId,
            background: document.hidden,
            userAgent: navigator.userAgent,
            language: navigator.language,
            browser: getBrowserName(),
            os: getOSName(),
            deviceModel: getDeviceModel(),
            deviceType: getDeviceType(),
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            countryCode: userLocation.country_code,
            ipAddress: userLocation.ip
        }
    );
}

/**
 * Log that a refresh token failed and we are kicking the user out. Of course this will only work if authToken is still valid.
 * @param {string} seriesId Login token series id.
 * @param {string} errorMessage Message from error that was thrown when request failed.
 * @returns 
 */
export const logForcedLogout = async (seriesId, errorMessage) => {
    return athenaAuthRequest(
        'web_forced_logout',
        {
            seriesId,
            error: errorMessage
        }
    );
}

/**
 * Log error to Athena.
 * @param {string} message Error message.
 * @param {import('error-stack-parser').StackFrame[]} trace Stack trace.
 * @param {object} [info] Additional info that will be merged into data logged to Athena.
 * @param {string} [info.identifier] Identifier for where error came from. Can make searching for specific errors in Athena easier.
 * @returns {Promise}
 */
// Including "toAthena" in the name so it doesn't clash with `logError` in /utils/errors
export const logErrorToAthena = async (message, trace, info = {}) => {
    return athenaAuthRequest(
        'web_error',
        {
            message,
            trace: typeof trace === 'string' ? trace : JSON.stringify(trace),
            href: window.location.href,
            ...info
        }
    )
}

/**
 * Log timings for starting up a camera's live stream.
 * @param {string} deviceUidd Camera Uidd.
 * @param {boolean} localLive Was this through local live rather than Wowza?
 * @param {object} infoToLog
 * @param {number} infoToLog.startTime Timestamp of when process started.
 * @param {number} infoToLog.endTime Timestamp of when log period ended (e.g. video started playing).
 * @param {number} infoToLog.timeTaken `endTime` subtract `startTime`.
 * @param {string} [infoToLog.feature] Which feature is using the live stream? E.g. "singlecam"
 * @param {object} infoToLog.initialValues Initial values of key variables when process started.
 * @param {object[]} infoToLog.events Events that occured during process, e.g. camera starts live streaming.
 *                                    Each event object should contain `event` name and `time` timestamp,
 *                                    alongside any other relevant info.
 * @param {boolean} infoToLog.localLiveAvailable Does this camera offer local live?
 * @param {boolean} infoToLog.success Did we play video successfully?
 * @param {object[]} infoToLog.data Array of timings in old format that matches mobile app so our logs are
 *                                  still backwards compatible with Rob D's queries.
 * @param {object[]} events Timings of each step.
 * @returns Axios promise.
 */
export const logLiveStreamStartup = (deviceUidd, localLive, infoToLog) => {
    return athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: localLive ? 'view_local_live' : 'view_live',
            ...infoToLog
        }
    );
}

/**
 * Log periodic info on a live stream.
 * @param {string} deviceUidd Camera uidd.
 * @param {object} infoToLog Object containing info about stream duration and buffering.
 * @returns Axios promise.
 */
export const logLiveStreamSummary = (deviceUidd, infoToLog) => {
    return athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: 'live_stream_summary',
            ...infoToLog
        }
    );
}

/**
 * Log instances of live feed buffering to Athena.
 * @param {string} deviceUidd Camera Uidd.
 * @param {string} [feature] Which feature is using the live stream? E.g. "multicam"
 * @param {number} startTime Time when we started tracking buffering.
 * @param {object[]} bufferingLogs Instances of buffering. Each element object will include start time, duration, and source (local or Wowza).
 * @param {number} endTime Time when we stopped tracking buffering.
 * @returns 
 */
export const logLiveStreamBuffering = (deviceUidd, feature, startTime, bufferingLogs, endTime) =>
    athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: 'live_buffering',
            startTime,
            buffering: bufferingLogs,
            endTime,
            feature
        }
    );

/**
 * Log that an active live stream dropped back to loading state.
 * @param {string} deviceUidd Camera Uidd.
 * @param {object} infoToLog Additional context.
 * @returns Axios promise.
 */
export const logLiveStreamLoadingDrop = (deviceUidd, infoToLog = {}) =>
    athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: 'live_loading_drop',
            ...infoToLog
        }
    );

/**
 * Log where live stream had to fallback from local live to cloud.
 * @param {string} deviceUidd Camera Uidd.
 * @param {string} localLiveAddress The address at which the local live server was found.
 * @param {obejct[]} errors Errors that occurred whilst trying to start the stream.
 * @returns Axios promise.
 */
export const logLocalLiveFallbackToCloud = (deviceUidd, localLiveAddress, errors) =>
    athenaAuthRequest(
        'local_live_fallback',
        {
            device: deviceUidd,
            localLiveAddress,
            errors
        }
    );

/**
 * Log result of checking whether local live server is reachable.
 * @param {boolean} success Is the local live server we were looking for available?
 * @param {string} message Extra info.
 * @param {object[]} errors Any errors we want recorded which occurred in testing local live addresses.
 * @returns 
 */
export const logLocalLiveAvailable = (success, message, errors) =>
    athenaAuthRequest(
        'local_live_test',
        {
            success,
            message,
            errors
        }
    );

/**
 * Log that a camera task was sent. Not currently used for all camera tasks but could be.
 * @param {string} cameraUidd Camera uidd.
 * @param {string} command Camera task.
 * @returns 
 */
export const logCameraTaskSent = (cameraUidd, command) => {
    return athenaAuthRequest(
        'web_camera_task',
        {
            device: cameraUidd,
            command
        }
    );
}
    

/**
 * Track alert link click through rate
 * @param {string} deviceUidd Camera Uidd.
 * @param {number} eventId Event id
 * @param {string} source Currently only email, possibly will be extended for push notifications as well
 * @returns 
 */
 export const logEventOpen = (deviceUidd, eventId, source="email") => {
    athenaAuthRequest(
        'alert_event',
        {
            device: deviceUidd,
            eventId,
            source
        }
    );
}

/**
 * Log timings for viewing recordedVideo.
 * @param {string} deviceUidd Camera Uidd.
 * @param {number} videoStartTime Timestamp of start of video.
 * @param {number} videoEndTime Timestamp of end of video.
 * @param {object} infoToLog
 * @param {boolean} infoToLog.success Was video viewed successfully?
 * @param {number} infoToLog.timeTaken Time taken in total to play the video (or to declare a failure) in milliseconds.
 * @param {{ event: string, timestamp: number, timeSinceStart: number, info?: object }[]} infoToLog.events Timings of each step towards playing the video.
 * @param {string} infoToLog.failureReason Failure reason if we were unable to play the video.
 * @param {string} [infoToLog.feature] Which web page or feature requested this video?
 * @param {boolean} [infoToLog.initialCanPlayH265] When we first tried to play the video, did we think this device and browser could play H265?
 * @returns Axios promise.
 */
export const logRecordedVideoView = (deviceUidd, videoStartTime, videoEndTime, infoToLog) =>
    athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: 'view_recorded_video',
            videoStartTime,
            videoEndTime,
            ...infoToLog
        }
    );
    
/**
 * Log that an event was downloaded and how long it took.
 * @param {string} deviceUidd Camera Uidd.
 * @param {number} eventId Event id.
 * @param {object[]} timings Timings of each step.
 * @returns 
 */
export const logEventDownload = (deviceUidd, eventId, timings) =>
    athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: 'download_event',
            event: eventId,
            data: timings,
        }
    );
    
/**
 * Log to Athena that a chunk was requested.
 * @param {number} startTime Timestamp when loading started.
 * @param {number} cutoffTime Timestamp when threshold was reached to consider it "stuck loading" and this log was triggered.
 * @param {object} startState Copy of any useful state data when loading started.
 * @param {object} endState Copy of any useful state data at `cutoffTime`.
 * @returns 
 */
export const logPlaybackStuckLoading = (startTime, cutoffTime, startState, endState) => {
    return athenaAuthRequest(
        'playback_loading',
        {
            startTime,
            cutoffTime,
            startState,
            endState
        }
    );
}

/**
 * Log to Athena that a chunk was requested.
 * @param {string} deviceUidd Device uidd.
 * @param {number} chunkStartTime Start time of chunk in ms.
 * @param {number} chunkEndTime End time of chunk in ms.
 * @param {number} timeTaken Time between request being sent and it returning in ms.
 * @param {number} httpStatus Request HTTP response status.
 * @param {object} chunkInfo Extra info on chunk.
 * @param {number} [chunkInfo.start] Number of seconds after requested start time that returned video starts.
 * @param {number} [chunkInfo.offset] Number of seconds before requested start time that returned video starts.
 * @param {number[][]} [chunkInfo.padding] Sections of video that are padded.
 * @param {string} [chunkInfo.codec] Video codec.
 * @param {boolean} [chunkInfo.transcoded] Was the video transcoded to this codec?
 * @param {('l', 't')} [chunkInfo.sourceServer] Which server was used?
 * @returns Axios promise.
 */
export const logPlaybackChunkFetched = (deviceUidd, chunkStartTime, chunkEndTime, timeTaken, httpStatus, chunkInfo = {}) => {
    return athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: 'build_playback_chunk',
            chunkStartTime,
            chunkEndTime,
            timeTaken,
            httpStatus,
            chunkStart: chunkInfo?.start,
            chunkSeek: chunkInfo?.offset,
            chunkPadding: chunkInfo?.padding,
            chunkCodec: chunkInfo?.codec,
            chunkTranscoded: chunkInfo?.transcoded,
            chunkSourceServer: chunkInfo?.sourceServer
        }
    );
}

/**
 * Log that the browser reported a media error when trying to play playback chunk.
 * @param {string} deviceUidd Device uidd.
 * @param {number} chunkStartTime Start time of chunk in ms.
 * @param {number} chunkEndTime End time of chunk in ms.
 * @param {any} info Any info reported by mpegts.js.
 * @returns Axios promise.
 */
export const logPlaybackChunkMediaError = (deviceUidd, chunkStartTime, chunkEndTime, info) => {
    return athenaAuthRequest(
        'playback_chunk_media_error',
        {
            device: deviceUidd,
            chunkStartTime,
            chunkEndTime,
            info
        }
    );
}

/**
 * Log that the user changed a filter.
 * @param {string} feature Which page/feature is the filter from?
 * @param {object} filterValues Values of all relevant filters.
 * @param {object} [additionalInfo] Any additional info to include.
 * @param {string[]} [additionalInfo.filtersChanged] Names of filters which changed.
 * @returns Axios promise.
 */
export const logFilterChanged = (feature, filterValues, additionalInfo = {}) => {
    return athenaAuthRequest(
        'web_filter_changed',
        {
            feature,
            filterValues,
            ...additionalInfo
        }
    );
}

/**
 * @typedef LiveModeToggle
 * @type {object}
 * @property {('stills', 'live')} newMode Mode toggled to.
 * @property {number} time Timestamp of change.
 * @property {number} timeSinceLastToggle Time since last toggle was made, or time since session started if this is first toggle.
 */

/**
 * Log that the user toggled between live stills and video modes.
 * @param {('stills', 'video')} newMode Mode toggled to.
 * @param {number} time Timestamp of toggle.
 * @param {number} timeSinceLastToggle Time in ms since previous toggle was made, or since session started if this is first toggle.
 * @param {('stills', 'video')} initialMode Mode the session started in.
 * @param {number} sessionStartTime Timestamp of when session started.
 * @param {LiveModeToggle[]} allToggles Record of all toggles in this session.
 * @param {number} currentSelectedCameras Number of cameras selected at time toggle made.
 * @param {number} currentTotalCameras Total number of cameras available to user at time toggle made.
 * @returns Axios promise.
 */
export const logLiveModeToggled = (newMode, time, timeSinceLastToggle, initialMode, sessionStartTime, allToggles, currentSelectedCameras, currentTotalCameras) => {
    return athenaAuthRequest(
        'timing',
        {
            action: 'live_mode_toggle',
            newMode,
            time,
            timeSinceLastToggle,
            initialMode,
            sessionStartTime,
            allToggles,
            currentSelectedCameras,
            currentTotalCameras
        }
    );
}

/**
 * Log periodic info for a camera feed in live stills mode.
 * @param {string} deviceUidd Camera uidd.
 * @param {object} infoToLog Object containing info about total duration and number of stills.
 * @returns Axios promise.
 */
export const logLiveStillsSummary = (deviceUidd, infoToLog) => {
    return athenaAuthRequest(
        'timing',
        {
            device: deviceUidd,
            action: 'live_stills_summary',
            ...infoToLog
        }
    );
}

    
/**
 * Log changes to viewer timezone selection
 * @param {string} timezone Timezone selected
 * @param {Array} available List of available timezones to choose from
 * @param {string} page Page on which it was used
 * @returns 
 */
 export const logTzChange = (timezone, available, page) =>
 athenaAuthRequest(
     'timezone',
     {
        action: 'change_timezone',
        to: timezone,
        available,
        page
    }
 );

 /**
  * Log invalid timezone format error
  * @param {string} timezone Timezone selected
  * @returns 
  */
  export const logTzFormatError = (timezone) =>
  athenaAuthRequest(
      'timezone',
      {
         action: 'invalid_timezone',
         tz: timezone,
     }
  );

export const logSessionFetch = (uidds, start, end) => {
    athenaAuthRequest(
        'get_sessions',
        {
            uidds,
            start,
            end
        }
    );
};

// install

const adapterResponseStates = {
    200: 'adapter_found',
    500: 'cloud_id_servers_screwed',
    404: 'cloud_id_not_found',
    403: 'cloud_id_already_linked',
    400: 'cloud_id_general_error',
    401: 'cloud_id_unauthorised_error'
};

const athenaInstallLoggerRequest = ({ installInstance, ...val }) => 
    athenaAuthRequest(
        'ca_setup', 
        {
            install_instance_id: installInstance?.id, 
            install_instance_start: installInstance?.start, 
            owner: installInstance?.owner,
            cloudId: installInstance?.cloudId, 
            ...val 
        }
    );

export const logIoTConnectionTimeout = () => {
    athenaInstallLoggerRequest({ action: 'iot_connection_timeout' });
};

export const logAdapterSearch = (installInstance, responseState, redirect) => {
    athenaInstallLoggerRequest({
        action: adapterResponseStates[responseState],
        installInstance,
        redirect
    });
};

export const logHelloAction = (installInstance, helloState, other_info) =>  {
    athenaInstallLoggerRequest({
        action: helloState ? 'hello_action_success' : 'hello_action_failure',
        installInstance,
        ...other_info
    });
};

export const logUnlinkAction = (installInstance, unlinkState) =>  {
    athenaInstallLoggerRequest({
        action: unlinkState ? 'uninstall_action_success' : 'uninstall_action_failure',
        installInstance
    });
};

export const logDeviceAuth = (installInstance, shadow, deviceHostname, deviceIpv4, deviceIpv6, serial, model, type, activation = false, add_by_ip = false) => {
    let loggingPayload = {
        action: 'lookup_model',
        shadow: shadow,
        camera_or_xvr: {
            hostname: deviceHostname,
            ipv4: deviceIpv4 || '',
            ipv6: deviceIpv6 || '',
            serial,
            model,
            type,
            activation
        },
        add_by_ip,
        installInstance
    };
    athenaInstallLoggerRequest(loggingPayload);
};

export const logSetupAction = (installInstance, action, other_info) => {
    athenaInstallLoggerRequest({
        installInstance,
        action,
        ...other_info
    });
};
   
export const logSelectedCameras = (installInstance, selectedCams) => {
    let cams = [];
    for(let cam of selectedCams) {
        if(cam.device_kind === 'XVR') {
            cams.push(cam.serial + '@' + cam.channel);
        } else {
            cams.push(cam.serial);
        }
    }
    athenaInstallLoggerRequest({
        action: 'selected_cameras',
        cameras: cams,
        count: cams.length,
        installInstance
    });
};

export const logInstallAction = (installInstance, devices) => {
    athenaInstallLoggerRequest({
        action: 'install_action_sent',
        devices: devices.map(dev => ({long_serial: dev.serial, channel: dev.channel, key: dev.shadowKey})),
        count: devices.length,
        installInstance
    });
};

export const logConfigureParams = (installInstance, config) => {
    athenaInstallLoggerRequest({
        action: 'configure_parameters',
        installInstance,
        ...config
    });
};

export const logInstallError = (installInstance, payload, response) =>{
    athenaInstallLoggerRequest({
        action: 'install_failed',
        installInstance, 
        api_payload: payload,
        api_response: response
    });
};

export const logGeneralShadowUpdateError = (installInstance, step, errorInfo, other_info) => {
    athenaInstallLoggerRequest({
        action: 'iot_update_fail',
        installInstance,
        step: step,
        error: errorInfo,
        ...other_info
    });
};

export const logUserInfo = (installInstance, browser, locale, localTime) => {
    athenaInstallLoggerRequest({
        action: 'browser_info',
        installInstance, 
        browser: browser,
        locale: locale,
        localtime: localTime,
        localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
};

export const getCameraOrXvrLog = (addIpDevice) => {
    return {
        hostname: addIpDevice.shadowKey, 
        ipv4: addIpDevice.shadowKey, 
        serial: addIpDevice.info?.serial, 
        model: addIpDevice.info?.model,
        macAddress: addIpDevice.info?.macAddress
    };
};

export const logNetworkConnectionLost = (installInstance) => {
    athenaInstallLoggerRequest({
        action: 'network_connection_error',
        installInstance
    });
};

export const logPopupShown = (installInstance, type, other_info) => {
    athenaInstallLoggerRequest({
        action: 'popup',
        type,
        installInstance,
        ...other_info
    });
};

// unlinking

const athenaUnlinkLogerRequest = (installInstance, action, other_info={}) => 
    athenaAuthRequest(
        'unlink_adapter', { 
            action, 
            instal_instance_id: installInstance?.id, 
            install_instance_start: installInstance?.start, 
            owner: installInstance?.owner,
            cloudId: installInstance?.cloudId,  
            ...other_info
        }
    );

export const logUnlinkApiCall = (installInstance, status) => {
    if(status === 200) {
        athenaUnlinkLogerRequest(installInstance, 'unlink_success_200');
    } else {
        athenaUnlinkLogerRequest(installInstance, 'unlink_failure_' + status);
    }
};

export const logUninstallActionSent = (installInstance, res, other_info) => {
    athenaUnlinkLogerRequest(installInstance, res ? 'uninstall_action_sent_success' : 'uninstall_action_sent_failure', other_info);
};

export const logUninstallActionRemoved = (installInstance, res, other_info) => {
    athenaUnlinkLogerRequest(installInstance, res ? 'uninstall_action_remove_success' : 'uninstall_action_remove_failure', other_info);
};

export const logUnlinkConnection = (instance, other_info) => {
    athenaUnlinkLogerRequest(instance, 'unlink_connect', {...other_info});
}
/**
 * 
 * @param {string} action - Specifies the action about to be performed. Must be either 'uninstall', 'uninstall_complete' or 'delete'.
 * @param {string[]} devices - An array of UIDDs representing the devices to be processed. 
 * @returns {void} 
 */
export const logDashboardCameraAction = (action, devices, other_info) => {
    athenaAuthRequest(
        'cameras_action',
        {
            action,
            devices,
            other_info
        }
    );
};

export const logUninstallTask = (devices, info) => {
    logDashboardCameraAction('uninstall_task', devices, info);
};


/* settings */
export const logSettingUpdate = (devices, setting) => {
    athenaAuthRequest(
        'setting_update',
        {
            devices,
            setting
        }
    );
};

export const logSettingsPopup = (type, id, devices) => {
    athenaAuthRequest(
        'setting_popup',
        {
            type,
            id,
            devices
        }
    );
};

export const logSettingsLimitReached = (data) => {
    athenaAuthRequest(
        'setting_limit_reached',
        data
    );
}

/**
 * multiuser logs
 */

export const logMultiuserAction = (owner, action, details) => {
    athenaAuthRequest(
        'multiuser_action',
        {
            action,
            owner,
            ...details
        }
    );
};

/**
 * sharing logs
 */

export const logLinkCopied = (exp, access, method) => {
    athenaAuthRequest(
        'copy_link',
        {
            type: 'video',
            ...(exp === SHARING_LINK_NEVER_EXPIRE || access === "private" ? {exp: undefined} : {exp: exp}), 
            access,
            method
        }
    )
}

/**
 * talkback logs
 */

export const logTalkback = (action, devices, other_info) => {
    athenaAuthRequest(
        'talkback',
        {
            action,
            devices,
            other_info
        }
    );
};
